import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, where, orderBy, limit, getDocs, doc, getDoc } from 'firebase/firestore';
import SEO from '../components/SEO';

// Import komponen UI
import Hero from '../components/Hero';
import MitraIndustri from '../components/MitraIndustri';
import AnimatedSection from '../components/AnimatedSection';
import VideoSection from '../components/VideoSection';
import ShortVideoSection from '../components/ShortVideoSection'; // Import komponen baru
import HomeAgendaSection from '../components/HomeAgendaSection'; // Import Agenda Section
import WhyChooseUs from '../components/WhyChooseUs'; // Import WhyChooseUs
import { FaArrowRight } from 'react-icons/fa';
import './HomePage.css';

// Import Lightbox
import Lightbox from "yet-another-react-lightbox";
// import Captions from "yet-another-react-lightbox/plugins/captions";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen";
import Counter from "yet-another-react-lightbox/plugins/counter";
import "yet-another-react-lightbox/styles.css";
// import "yet-another-react-lightbox/plugins/captions.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import "yet-another-react-lightbox/plugins/counter.css";
// import Tilt from 'react-parallax-tilt'; // Removed Tilt

// Import Swiper React components
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/pagination';

const getCategoryColor = (categoryName) => {
  const colors = ['#e74c3c', '#3498db', '#2ecc71', '#f39c12', '#9b59b6', '#1abc9c', '#e67e22', '#34495e', '#d35400', '#c0392b'];
  if (!categoryName) return colors[0];
  let hash = 0;
  for (let i = 0; i < categoryName.length; i++) {
    hash = categoryName.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const HomePage = () => {
  const [pageData, setPageData] = useState(null);
  const [majors, setMajors] = useState([]);
  const [berita, setBerita] = useState([]);
  const [categoryColors, setCategoryColors] = useState({});
  const [prestasi, setPrestasi] = useState([]);
  const [alumniLulus, setAlumniLulus] = useState([]);
  const [testimoni, setTestimoni] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [partners, setPartners] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState(null);

  // State untuk Lightbox
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [imageIndex, setImageIndex] = useState(0);
  const [lightboxSlides, setLightboxSlides] = useState([]);

  const fetchData = useCallback(async () => {
    setError(null);
    try {
      // Fetch Hero and CTA data
      try {
        const pageContentRef = doc(db, 'pageContent', 'homePage');
        const pageContentSnap = await getDoc(pageContentRef);
        if (pageContentSnap.exists()) {
          setPageData(pageContentSnap.data());
        }
      } catch (e) { console.error("Error fetching page content:", e); }

      // Fetch Majors
      try {
        const majorsQuery = query(collection(db, 'majors'), orderBy('order', 'asc'));
        const majorsSnapshot = await getDocs(majorsQuery);
        setMajors(majorsSnapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) { console.error("Error fetching majors:", e); }

      // Fetch Berita Categories for Colors
      try {
        const beritaCategoriesQuery = query(collection(db, 'berita_kategori'));
        const beritaCategoriesSnapshot = await getDocs(beritaCategoriesQuery);
        const colorsMap = {};
        beritaCategoriesSnapshot.forEach(doc => {
          const data = doc.data();
          if (data.name && data.color) {
            colorsMap[data.name] = data.color;
          }
        });
        setCategoryColors(colorsMap);
      } catch (e) { console.error("Error fetching categories:", e); }

      // Fetch Berita
      try {
        const beritaQuery = query(collection(db, "berita"), where("status", "==", "published"), orderBy("date", "desc"), limit(6));
        const beritaSnapshot = await getDocs(beritaQuery);
        setBerita(beritaSnapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) { console.error("Error fetching news:", e); }

      // Fetch Prestasi
      try {
        const prestasiQuery = query(collection(db, 'prestasi'), orderBy('date', 'desc'), limit(6));
        const prestasiSnapshot = await getDocs(prestasiQuery);
        setPrestasi(prestasiSnapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) { console.error("Error fetching prestasi:", e); }

      // Fetch Alumni Lulus
      try {
        const alumniQuery = query(collection(db, 'alumni_lulus'), orderBy('date', 'desc'), limit(6));
        const alumniSnapshot = await getDocs(alumniQuery);
        setAlumniLulus(alumniSnapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) { console.error("Error fetching alumni lulus:", e); }

      // Fetch Testimoni Alumni (Isolated in try-catch to prevent page load failure if collection/index is missing)
      try {
        const testimoniQuery = query(collection(db, 'testimoni_alumni'), orderBy('order', 'asc'), limit(8));
        const testimoniSnapshot = await getDocs(testimoniQuery);
        setTestimoni(testimoniSnapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (testimonialsError) {
        console.error("Error fetching testimonials:", testimonialsError);
      }

      // Fetch Gallery (Albums)
      try {
        const galleryQuery = query(collection(db, 'galeri_kategori'), where("isVisible", "==", true));
        const gallerySnapshot = await getDocs(galleryQuery);
        let galleryData = gallerySnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

        // Sort by date descending (Newest first)
        galleryData.sort((a, b) => {
          const dateA = a.date ? new Date(a.date) : new Date(0);
          const dateB = b.date ? new Date(b.date) : new Date(0);
          return dateB - dateA;
        });

        // Limit to 8 items for homepage
        setGallery(galleryData.slice(0, 8));
      } catch (e) { console.error("Error fetching gallery:", e); }

      // Fetch Mitra Industri
      try {
        const partnersQuery = query(collection(db, 'mitraIndustri'), orderBy('order', 'asc'));
        const partnersSnapshot = await getDocs(partnersQuery);
        setPartners(partnersSnapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) { console.error("Error fetching partners:", e); }

    } catch (err) {
      console.error("Critical error in fetchData:", err);
      setError("Gagal memuat beberapa data. Silakan muat ulang halaman.");
    } finally {
      setPageLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fungsi untuk menentukan apakah slider harus loop
  const shouldLoop = (items, slidesPerView) => {
    // Loop jika jumlah item setidaknya 2x jumlah slide yang terlihat di breakpoint terbesar
    // atau jika jumlah item lebih besar dari slidesPerView
    return items.length >= (slidesPerView * 2) || items.length > slidesPerView;
  }

  // Section wrapper removed

  const openLightbox = (index, type = 'prestasi') => {
    const data = type === 'prestasi' ? prestasi : type === 'alumniLulus' ? alumniLulus : testimoni;
    setLightboxSlides(data.map(item => ({
      src: item.image,
      title: item.title,
    })));
    setImageIndex(index);
    setLightboxOpen(true);
  };

  const sliderSettings = {
    modules: [Navigation, Pagination, Autoplay],
    spaceBetween: 30,
    pagination: { clickable: true },
    navigation: true,
    loop: true,
    autoplay: {
      delay: 4000,
      disableOnInteraction: false,
    },
    breakpoints: {
      320: { slidesPerView: 1 },
      640: { slidesPerView: 1 },
      768: { slidesPerView: 2 },
      1024: { slidesPerView: 3 },
    },
    centerInsufficientSlides: true
  };

  // Slider khusus testimoni agar lebih besar (2 per row di desktop)
  const testimoniSliderSettings = {
    ...sliderSettings,
    breakpoints: {
      ...sliderSettings.breakpoints,
      1024: { slidesPerView: 2 },
    }
  };



  return (
    <>
      <SEO
        title="Beranda - SMK Pariwisata Sumbangsih"
        description="Selamat datang di website resmi SMK Pariwisata Sumbangsih. Sekolah kejuruan unggulan di bidang pariwisata dan perhotelan yang mencetak lulusan siap kerja."
      />
      <Hero
        images={pageData?.heroImages || []}
        title={pageData?.heroTitle || "Selamat Datang"}
        subtitle={pageData?.heroSubtitle || "Di Website SMK Pariwisata Sumbangsih"}
        loading={pageLoading}
      />

      {error && (
        <div className="container" style={{ padding: '20px 0' }}>
          <p className="submit-status error">{error}</p>
        </div>
      )}

      {/* Agenda Section */}
      <AnimatedSection>
        <HomeAgendaSection />
      </AnimatedSection>

      {/* Berita & Informasi Terkini Slider */}
      <AnimatedSection>
        <section className="section">
          <div className="container">
            <h2 style={{ color: 'inherit' }}>
              <span style={{ color: 'inherit' }}>Berita & </span>
              Informasi
            </h2>
            <Swiper
              {...sliderSettings}
              loop={shouldLoop(berita, sliderSettings.breakpoints['1024'].slidesPerView)}
              className="slider-container">
              {berita.map(item => (
                <SwiperSlide key={item.id}>
                  <Link to={`/berita/${item.slug}`} className="berita-preview-card">
                    <div className="berita-preview-image-wrapper">
                      <img src={item.image} alt={item.title} className="berita-preview-image" />
                      {item.category && <span className="berita-preview-category" style={{ backgroundColor: categoryColors[item.category] || getCategoryColor(item.category) }}>{item.category}</span>}
                    </div>
                    <div className="berita-preview-content">
                      <span className="berita-preview-date">{new Date(item.date).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                      <h3 className="berita-preview-title">{item.title}</h3>
                    </div>
                  </Link>
                </SwiperSlide>
              ))}
            </Swiper>
            <div className="berita-preview-footer">
              <Link to="/berita" className="btn-hero">Lihat Semua Berita</Link>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* Program Keahlian Slider */}
      <AnimatedSection>
        <section className="section">
          <div className="container">
            <h2 style={{ color: 'inherit' }}>
              <span style={{ color: 'inherit' }}>Program </span>
              Keahlian</h2>
            <Swiper
              {...sliderSettings}
              loop={shouldLoop(majors, sliderSettings.breakpoints['1024'].slidesPerView)}
              className="slider-container">
              {majors.map(major => (
                <SwiperSlide key={major.id} className="major-card-slide">
                  <div className="major-card h-100">
                    <img src={major.image} alt={major.title} className="major-image" />
                    <div className="major-content">
                      <h3>{major.title}</h3>
                      <p>{major.description.substring(0, 100)}...</p>
                      <Link to={`/jurusan/${major.id}`} className="btn-secondary">
                        Selengkapnya <FaArrowRight />
                      </Link>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </section>
      </AnimatedSection>

      {/* Mengapa Pilih Kami Section */}
      <AnimatedSection>
        <WhyChooseUs />
      </AnimatedSection>

      {/* Prestasi Terbaru Slider */}
      <AnimatedSection>
        <section className="section">
          <div className="container">
            <h2 style={{ color: 'inherit' }}>Prestasi</h2>
            <Swiper
              {...sliderSettings}
              loop={shouldLoop(prestasi, sliderSettings.breakpoints['1024'].slidesPerView)}
              className="slider-container">
              {prestasi.map((item, index) => (
                <SwiperSlide key={item.id}>
                  <div
                    className="prestasi-home-card"
                    onClick={() => openLightbox(index, 'prestasi')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="prestasi-home-image-wrapper">
                      <img src={item.image} alt={item.title} className="prestasi-home-image" loading="lazy" />
                    </div>
                    <div className="prestasi-home-content">
                      <span className="prestasi-home-date">{new Date(item.date.toDate()).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                      <h3 className="prestasi-home-title">{item.title}</h3>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
            <div className="berita-preview-footer">
              <Link to="/prestasi" className="btn-hero">Lihat Semua Prestasi</Link>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* Testimoni Alumni Section */}
      {testimoni.length > 0 && (
        <AnimatedSection>
          <section className="section">
            <div className="container">
              <h2 style={{ color: 'inherit' }}>Testimoni Alumni</h2>

              <Swiper
                {...testimoniSliderSettings}
                loop={shouldLoop(testimoni, testimoniSliderSettings.breakpoints['1024'].slidesPerView)}
                className="slider-container"
              >
                {testimoni.map((item, index) => (
                  <SwiperSlide key={item.id}>
                    <div className="prestasi-home-card"
                      onClick={() => openLightbox(index, 'testimoni')}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="prestasi-home-image-wrapper">
                        <img src={item.image || 'https://via.placeholder.com/400x300'} alt={item.title} className="prestasi-home-image" loading="lazy" />
                      </div>
                      <div className="prestasi-home-content">
                        <span className="prestasi-home-date">
                          {item.date && (item.date.toDate ? new Date(item.date.toDate()).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }) : item.date)}
                        </span>
                        <h3 className="prestasi-home-title">{item.title}</h3>
                        {item.excerpt && (
                          <p className="testimonial-home-text" style={{ fontSize: '0.9rem', marginTop: '10px', color: '#555', fontStyle: 'italic', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', textAlign: 'center' }}>
                            "{item.excerpt}"
                          </p>
                        )}
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </section>
        </AnimatedSection>
      )}

      {/* Alumni Lulus Slider */}
      {alumniLulus.length > 0 && (
        <AnimatedSection>
          <section className="section">
            <div className="container">
              <h2 style={{ color: 'inherit' }}>Alumni Yang Melanjutkan Ke Perguruan Tinggi</h2>
              <Swiper
                {...sliderSettings}
                loop={shouldLoop(alumniLulus, sliderSettings.breakpoints['1024'].slidesPerView)}
                className="slider-container">
                {alumniLulus.map((item, index) => (
                  <SwiperSlide key={item.id}>
                    <div
                      className="prestasi-home-card"
                      onClick={() => openLightbox(index, 'alumniLulus')}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="prestasi-home-image-wrapper">
                        <img src={item.image} alt={item.title} className="prestasi-home-image" loading="lazy" />
                      </div>
                      <div className="prestasi-home-content">
                        <span className="prestasi-home-date">
                          {item.date && (item.date.toDate ? new Date(item.date.toDate()).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }) : item.date)}
                        </span>
                        <h3 className="prestasi-home-title">{item.title}</h3>
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
              <div className="berita-preview-footer">
                <Link to="/alumni-lulus" className="btn-hero">Lihat Semua Alumni Lulus</Link>
              </div>
            </div>
          </section>
        </AnimatedSection>
      )}

      {/* Galeri Sekolah Slider */}
      <AnimatedSection>
        <section className="section">
          <div className="container">
            <h2 style={{ color: 'inherit' }}>Galeri Kegiatan</h2>
            <Swiper
              modules={[Navigation, Pagination, Autoplay]}
              spaceBetween={20}
              loop={shouldLoop(gallery, 3)}
              autoplay={{ delay: 3000, disableOnInteraction: false }}
              breakpoints={{
                320: { slidesPerView: 1 },
                640: { slidesPerView: 2 },
                768: { slidesPerView: 3 }, // Adjusted to 3 for better card visibility
              }}
              centerInsufficientSlides={true}
              className="slider-container"
              style={{ paddingBottom: '50px' }}
            >
              {gallery.map(item => (
                <SwiperSlide key={item.id}>
                  <a
                    href={item.externalLink || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="gallery-preview-item"
                    onClick={(e) => {
                      if (!item.externalLink) {
                        e.preventDefault();
                      }
                    }}
                  >
                    <div className="gallery-image-wrapper">
                      <div className="gallery-badge">Album</div>
                      <img
                        src={item.imageUrl || 'https://via.placeholder.com/400x300?text=No+Cover'}
                        alt={item.name}
                        className="gallery-preview-image"
                      />
                    </div>
                    <div className="gallery-card-content">
                      <h4 className="gallery-card-title">{item.name}</h4>
                      {item.date && (
                        <p className="gallery-card-date">
                          {new Date(item.date).toLocaleDateString('id-ID', { year: 'numeric', month: 'long', day: 'numeric' })}
                        </p>
                      )}
                    </div>
                  </a>
                </SwiperSlide>
              ))}
            </Swiper>
            <div className="gallery-preview-footer">
              <Link to="/galeri" className="btn-hero">Lihat Galeri Lengkap</Link>
            </div>
          </div>
        </section>
      </AnimatedSection>


      {/* Video Youtube Section */}
      <AnimatedSection>
        <VideoSection />
      </AnimatedSection>

      {/* Short Video Section */}
      <AnimatedSection>
        <ShortVideoSection />
      </AnimatedSection>

      {/* Mitra Industri Section */}
      <AnimatedSection>
        <section className="section-mitra">
          <MitraIndustri partners={partners} loading={pageLoading} />
        </section>
      </AnimatedSection>

      {/* Call to Action Section */}
      <AnimatedSection>
        <section
          className="section text-center cta-section"
          style={pageData?.ctaBgUrl ? {
            backgroundImage: `linear-gradient(rgba(0,0,0,0.65), rgba(0,0,0,0.65)), url(${pageData.ctaBgUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed' // Efek sedikit parallax statis
          } : {}}
        >
          <div className="container">
            <h2>Siap Bergabung dengan Kami di Sekolah?</h2>
            <p>Jadilah bagian dari generasi profesional perhotelan dan usaha layanan pariwisata berikutnya. Daftar sekarang juga!</p>
            <Link to="/spmb" className="btn-hero">Informasi Pendaftaran</Link>
          </div>
        </section>
      </AnimatedSection>

      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        index={imageIndex}
        slides={lightboxSlides}
        plugins={[Zoom, Thumbnails, Fullscreen, Counter]}
        click={(api) => api.toggleControls()}
        zoom={{
          scrollToZoom: true,
          maxZoomPixelRatio: 5,
        }}
        thumbnails={{
          position: "bottom",
          width: 80,
          height: 60,
          border: 1,
          borderRadius: 4,
          padding: 4,
          gap: 16,
          showToggle: true, // Ensure toggle button is shown
        }}
        counter={{ container: { style: { top: "unset", bottom: 0 } } }}
        animation={{
          zoom: 300,
        }}
      />
    </>
  );
};

export default HomePage;